import React from 'react';
import { Radar, MapPin, ClipboardList, Navigation, ArrowRight } from 'lucide-react';

const ProspectingWorkflow = () => {
  const steps = [
    {
      number: "1",
      icon: <Radar className="w-5 h-5 text-[#2DD4BF]" />,
      title: "Nearby Scan",
      description: "Arrive at a store and hit 'Nearby.' The app instantly scans the area to see if the store is already in your system or a new lead."
    },
    {
      number: "2",
      icon: <MapPin className="w-5 h-5 text-[#2DD4BF]" />,
      title: "One-Tap Add",
      description: "Not in the list? Hit 'Use Current Location.' The app pulls the exact GPS coordinates and address automatically. No manual typing."
    },
    {
      number: "3",
      icon: <ClipboardList className="w-5 h-5 text-[#2DD4BF]" />,
      title: "Context & Notes",
      description: "Add notes on the spot. Capture the store's potential, current stock, and manager names while you're standing there."
    },
    {
      number: "4",
      icon: <Navigation className="w-5 h-5 text-[#2DD4BF]" />,
      title: "Assign Route",
      description: "Immediately assign the new store to a sales route. It turns from a 'Prospect' into a scheduled stop in your distribution cycle. See a route on map or see the master map with all the routes, change store route with a click."
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-black text-white border-t border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        
        {/* Header - Sizes optimized for mobile */}
        <div className="text-center mb-12 md:mb-20">
          <span className="bg-[#2DD4BF] text-black text-[10px] font-bold px-3 py-1 uppercase tracking-widest rounded-sm">
            THE PROSPECTING WORKFLOW
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mt-6 mb-2 text-white leading-tight">Never Miss a Lead.</h2>
          <h3 className="text-3xl md:text-5xl font-bold text-gray-600">Add any store in seconds.</h3>
        </div>

        {/* Steps Grid - Stacked on mobile, 2-col on tablet, 4-col on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-4 relative">
          {steps.map((step, index) => (
            <div key={index} className="relative group">
              <div className="bg-[#111111] border border-white/5 p-6 md:p-8 h-full rounded-sm flex flex-col transition-all hover:border-[#2DD4BF]/20">
                
                {/* Number and horizontal line */}
                <div className="flex items-center mb-6 md:mb-8">
                  <div className="bg-[#2DD4BF] text-black w-10 h-10 flex-shrink-0 flex items-center justify-center font-bold text-lg">
                    {step.number}
                  </div>
                  <div className="flex-grow h-[1px] bg-gray-800 ml-4"></div>
                </div>

                {/* Icon Box */}
                <div className="bg-white/5 w-12 h-12 flex items-center justify-center mb-6 border border-white/10 rounded-sm">
                  {step.icon}
                </div>

                {/* Content */}
                <h4 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">{step.title}</h4>
                <p className="text-gray-400 text-sm md:text-base leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Connecting Arrow - Desktop only (lg screens and up) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 bg-[#111111] border border-white/10 p-1 rounded-sm">
                  <ArrowRight className="w-4 h-4 text-[#2DD4BF]" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProspectingWorkflow;